import api from "./api";
import commonApi from "../common/api";
import log from "loglevel";

const state = {
  commentList: [],
  identificationCode: "",
  updatedThumbnailIdentificationCode: "",
  defaultThumbnailOriginalImageUrl: "",
  defaultThumbnailCroppedImageUrl: "",
  userThumbnailOriginalImageUrl: "",
  userThumbnailCroppedImageUrl: "",
  initialThumbnailImageUrl: "",
  updatedSocialMediaImageIdentificationCode: "",
  defaultOriginalSocialMediaImageUrl: "",
  defaultCroppedSocialMediaImageUrl: "",
  userOriginalSocialMediaImageUrl: "",
  userCroppedSocialMediaImageUrl: "",
  initialSocialMediaImageUrl: "",
  previewUrl: ""
};

// getters
const getters = {
  thumbnailOriginalImageUrl: state => {
    if (state.userThumbnailOriginalImageUrl) {
      return state.userThumbnailOriginalImageUrl;
    } else if (state.defaultThumbnailOriginalImageUrl) {
      return state.defaultThumbnailOriginalImageUrl;
    } else {
      return "";
    }
  },
  thumbnailCroppedImageUrl: state => {
    if (state.userThumbnailCroppedImageUrl) {
      return state.userThumbnailCroppedImageUrl;
    } else if (state.defaultThumbnailCroppedImageUrl) {
      return state.defaultThumbnailCroppedImageUrl;
    } else {
      return "";
    }
  },
  updatedContentsThumbnailParams: state => ({
    identificationCode: state.identificationCode
  }),
  updatedContentsThumbnailCompleteParams: state => ({
    identificationCode: state.identificationCode,
    updatedThumbnailIdentificationCode: state.updatedThumbnailIdentificationCode
  }),
  updatedResetContentsThumbnailParams: state => ({
    identificationCode: state.identificationCode,
    updatedThumbnailIdentificationCode: state.updatedThumbnailIdentificationCode
  }),
  originalSocialMediaImageUrl: state => {
    if (state.userOriginalSocialMediaImageUrl) {
      return state.userOriginalSocialMediaImageUrl;
    } else if (state.defaultOriginalSocialMediaImageUrl) {
      return state.defaultOriginalSocialMediaImageUrl;
    } else {
      return "";
    }
  },
  croppedSocialMediaImageUrl: state => {
    if (state.userCroppedSocialMediaImageUrl) {
      return state.userCroppedSocialMediaImageUrl;
    } else if (state.defaultCroppedSocialMediaImageUrl) {
      return state.defaultCroppedSocialMediaImageUrl;
    } else {
      return "";
    }
  },
  updatedSocialMediaImageParams: state => ({
    identificationCode: state.identificationCode
  }),
  updatedSocialMediaImageCompleteParams: state => ({
    identificationCode: state.identificationCode,
    updatedSocialMediaImageIdentificationCode:
      state.updatedSocialMediaImageIdentificationCode
  }),
  updatedResetSocialMediaImageParams: state => ({
    identificationCode: state.identificationCode,
    updatedSocialMediaImageIdentificationCode:
      state.updatedSocialMediaImageIdentificationCode
  })
};

// actions
const actions = {
  async saveSettingsChanges(context, params) {
    try {
      const res = await api.putSettingsChanges(params);
      return res.status;
    } catch (error) {
      log.error("saveSettingsChanges error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    }
  },
  async deleteContents(context, params) {
    try {
      const res = await api.deleteContents(params);
      return res.status;
    } catch (error) {
      log.error("deleteContents error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    }
  },
  async postUpdatedContentsThumbnail(context, blob) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      const postRes = await api.postUpdatedContentsThumbnail(
        context.getters.updatedContentsThumbnailParams
      );
      context.commit(
        "setUpdatedThumbnailIdentificationCode",
        postRes.data.updatedThumbnailIdentificationCode
      );
      await commonApi.postS3Image(postRes.data.signedUrl, blob);
      const completeRes = await api.postUpdatedContentsThumbnailComplete(
        context.getters.updatedContentsThumbnailCompleteParams
      );
      context.commit(
        "updateUserThumbnailCroppedImageUrl",
        completeRes.data.imageUrl
      );

      return completeRes;
    } catch (error) {
      log.error("postThumbnailImage error:", error);
      context.commit("common/setError", error, { root: true });
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async deleteUpdatedContentsThumbnail(context) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      await api.deleteUpdatedContentsThumbnail(
        context.getters.updatedResetContentsThumbnailParams
      );
      context.commit("updateUserThumbnailCroppedImageUrl", "");
    } catch (error) {
      log.error("deleteThumbnailImage error:", error);
      context.commit("common/setError", error, { root: true });
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async postUpdatedSocialMediaImage(context, blob) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      const postRes = await api.postUpdatedSocialMediaImage(
        context.getters.updatedSocialMediaImageParams
      );
      context.commit(
        "setUpdatedSocialMediaImageIdentificationCode",
        postRes.data.updatedSocialMediaImageIdentificationCode
      );
      await commonApi.postS3Image(postRes.data.signedUrl, blob);
      const completeRes = await api.postUpdatedSocialMediaImageComplete(
        context.getters.updatedSocialMediaImageCompleteParams
      );
      context.commit(
        "updateUserCroppedSocialMediaImageUrl",
        completeRes.data.imageUrl
      );

      return completeRes;
    } catch (error) {
      log.error("postUpdatedSocialMediaImage error:", error);
      context.commit("common/setError", error, { root: true });
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async deleteUpdatedSocialMediaImage(context) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      await api.deleteUpdatedSocialMediaImage(
        context.getters.updatedResetSocialMediaImageParams
      );
      context.commit("updateUserCroppedSocialMediaImageUrl", "");
    } catch (error) {
      log.error("deleteUpdatedSocialMediaImage error:", error);
      context.commit("common/setError", error, { root: true });
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async saveExternalClients(context, params) {
    try {
      for (const param of params) {
        if (param.isEnabled) {
          await api.putExternalClients({
            contentsId: param.contentsId,
            clientId: param.clientId
          });
        } else {
          await api.deleteExternalClients({
            contentsId: param.contentsId,
            clientId: param.clientId
          });
        }
      }

      return 0;
    } catch (error) {
      log.error("deleteContents error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    }
  },
  async postContentsAddLink(context, params) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      return await api.postContentsAddLink(params);
    } catch (error) {
      log.error("postContentsAddLink error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async saveContentsAddLink(context, params) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      const res = await api.putContentsAddLink(params);
      return res.status;
    } catch (error) {
      log.error("saveContentsAddLink error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async deleteContentsAddLink(context, params) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      const res = await api.deleteContentsAddLink(params);
      return res.status;
    } catch (error) {
      log.error("deleteContentsAddLink error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async saveContentsLinkSettings(context, params) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      const res = await api.putContentsLinkSettings(params);
      return res.status;
    } catch (error) {
      log.error("saveContentsListSettings error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async saveContentsLinkSettingsPreview(context, params) {
    try {
      context.commit("common/setIsBusy", true, { root: true });
      const res = await api.putContentsLinkSettingsPreview(params);
      context.commit("updatePreviewUrl", res.data.viewerUrl);
      return { status: res.status, viewerUrl: res.data.viewerUrl };
    } catch (error) {
      log.error("saveContentsListSettingsPreview error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    } finally {
      context.commit("common/setIsBusy", false, { root: true });
    }
  },
  async getContentsPageList(context, params) {
    try {
      const res = await api.postDraftContentsLinkSettings(params);
      return {
        status: res.status,
        draftId: res.data.draftId,
        imageUrls: res.data.imageUrls,
        scrambleWidth: res.data.scrambleWidth,
        scrambleHeight: res.data.scrambleHeight,
        draftContentsLinks: res.data.draftContentsLinks
      };
    } catch (error) {
      log.error("getContentsPageList error:", error);
      context.commit("common/setError", error, { root: true });
      return error.status;
    }
  }
};

//mutations
const mutations = {
  setMyContentsThumbnailImageUrl(state, payload) {
    state.initialThumbnailImageUrl = payload;
    state.defaultThumbnailCroppedImageUrl =
      payload +
      "?" +
      Math.random()
        .toString(36)
        .slice(-10);
    state.defaultThumbnailOriginalImageUrl =
      payload +
      "?" +
      Math.random()
        .toString(36)
        .slice(-10);
  },
  setContentsIdentificationCode(state, payload) {
    state.identificationCode = payload;
  },
  updateUserThumbnailCroppedImageUrl(state, payload) {
    state.userThumbnailCroppedImageUrl = payload;
  },
  setUpdatedThumbnailIdentificationCode(state, payload) {
    state.updatedThumbnailIdentificationCode = payload;
  },
  updateUserThumbnailOriginalImageUrl(state, payload) {
    state.userThumbnailOriginalImageUrl = payload;
  },
  updateDefaultThumbnailOriginalImageUrlQueryString(state) {
    state.defaultThumbnailOriginalImageUrl =
      state.initialThumbnailImageUrl +
      "?" +
      Math.random()
        .toString(36)
        .slice(-10);
  },
  setSocialMediaImageUrl(state, payload) {
    state.initialSocialMediaImageUrl = payload;
    state.defaultCroppedSocialMediaImageUrl =
      payload +
      "?" +
      Math.random()
        .toString(36)
        .slice(-10);
    state.defaultOriginalSocialMediaImageUrl =
      payload +
      "?" +
      Math.random()
        .toString(36)
        .slice(-10);
  },
  updateUserCroppedSocialMediaImageUrl(state, payload) {
    state.userCroppedSocialMediaImageUrl = payload;
  },
  setUpdatedSocialMediaImageIdentificationCode(state, payload) {
    state.updatedSocialMediaImageIdentificationCode = payload;
  },
  updateUserOriginalSocialMediaImageUrl(state, payload) {
    state.userOriginalSocialMediaImageUrl = payload;
  },
  updateDefaultOriginalSocialMediaImageUrlQueryString(state) {
    state.defaultOriginalSocialMediaImageUrl =
      state.initialSocialMediaImageUrl +
      "?" +
      Math.random()
        .toString(36)
        .slice(-10);
  },
  updatePreviewUrl(state, payload) {
    state.previewUrl = payload;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
